import { authService } from './authService'
import { getTenantId } from '../utils/tenantUtils'
import { withApiBaseUrl } from '../lib/apiBaseUrl'

const BASE = '/api/v1/red-envelope'

export type RedEnvelopePaymentType = 'cash' | 'points'

export interface RedEnvelopeMode {
  key: string
  label: string
  envelopeCount: number
  costPoints: number
  costCash: number
}

function headers() {
  const token = authService.getToken()
  const tenantId = getTenantId()
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
  }
}

async function parseJson<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    const err = new Error(
      (data as { message?: string })?.message || `HTTP ${response.status}`,
    ) as Error & { code?: string; status?: number }
    err.code = (data as { code?: string })?.code
    err.status = response.status
    throw err
  }
  return data as T
}

export async function fetchRedEnvelopePlayConfig() {
  const response = await fetch(withApiBaseUrl(`${BASE}/play-config`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: { userEntryVisible: boolean; modes: RedEnvelopeMode[] }
  }>(response)
}

export async function startRedEnvelopeRound(modeKey: string, paymentType: RedEnvelopePaymentType) {
  const response = await fetch(withApiBaseUrl(`${BASE}/rounds/start`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ modeKey, paymentType }),
  })
  return parseJson<{
    success: boolean
    data: {
      roundId: string
      envelopeCount: number
      paymentType: RedEnvelopePaymentType
      costAmount: number
      opensRemaining: number
      cells: Array<{ index: number; letterIndex: number; openable: boolean; opened: boolean }>
    }
  }>(response)
}

export async function openRedEnvelopeCell(roundId: string, cellIndex: number) {
  const response = await fetch(withApiBaseUrl(`${BASE}/rounds/${roundId}/open`), {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ cellIndex }),
  })
  return parseJson<{
    success: boolean
    data: {
      amount: number
      currency: RedEnvelopePaymentType
      opensRemaining: number
      totalEarned: number
    }
  }>(response)
}

export async function fetchRedEnvelopeRoundStatus(roundId: string) {
  const response = await fetch(withApiBaseUrl(`${BASE}/rounds/${roundId}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: {
      roundId: string
      status: 'active' | 'completed' | 'cancelled' | string
      opensRemaining: number
      envelopeCount: number
      paymentType: RedEnvelopePaymentType
      costAmount: number
    }
  }>(response)
}

export async function revealRemainingRedEnvelope(roundId: string) {
  const response = await fetch(withApiBaseUrl(`${BASE}/rounds/${roundId}/reveal-remaining`), {
    method: 'POST',
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: {
      totalEarned: number
      paymentType: RedEnvelopePaymentType
      cells: Array<{ index: number; amount: number; currency: string; openable: boolean }>
    }
  }>(response)
}

export type RedEnvelopeLedgerBalance = {
  before?: number | null
  after?: number | null
  amount?: number
  transactionId?: string
  at?: string | null
}

export type RedEnvelopeMyRound = {
  roundId: string
  modeKey: string
  envelopeCount: number
  paymentType: RedEnvelopePaymentType
  costAmount: number
  status: 'active' | 'completed' | 'cancelled' | string
  creditedTotal: number
  openedCount: number
  debitBalance?: RedEnvelopeLedgerBalance | null
  refundBalance?: RedEnvelopeLedgerBalance | null
  createdAt: string
  updatedAt: string
  cells: Array<{
    index: number
    amount: number
    currency: string
    openedAt?: string | null
    credited?: boolean
    balanceBefore?: number | null
    balanceAfter?: number | null
  }>
}

export async function fetchMyRedEnvelopeRounds(params?: { page?: number; limit?: number }) {
  const page = params?.page || 1
  const limit = params?.limit || 20
  const qs = new URLSearchParams({ page: String(page), limit: String(limit) })
  const response = await fetch(withApiBaseUrl(`${BASE}/rounds/my?${qs}`), {
    headers: headers(),
  })
  return parseJson<{
    success: boolean
    data: RedEnvelopeMyRound[]
    pagination: { page: number; limit: number; total: number; totalPages: number }
  }>(response)
}
